import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { API } from "../App";
import { Lock, ArrowRight, AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import FuturaIcon from "../components/FuturaIcon";
import { useLanguage } from "../i18n/LanguageContext";
import { LanguageSelector } from "../components/LanguageSelector";
import axios from "axios";

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  const token = searchParams.get("token") || "";

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleReset = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword.length < 6) { setError(t("forgot_min_password")); return; }
    if (!/^[A-Za-z0-9]+$/.test(newPassword)) { setError(t("register_password_alphanumeric")); return; }
    if (newPassword !== confirmPassword) { setError(t("forgot_passwords_mismatch")); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: newPassword });
      setDone(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Error");
    } finally { setLoading(false); }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-[#EF4444] mx-auto mb-4" />
          <h1 className="font-['Outfit'] text-2xl font-semibold text-[#111111] mb-3">{t("reset_invalid_link")}</h1>
          <p className="text-[#6B7280] mb-6">{t("reset_invalid_desc")}</p>
          <Button onClick={() => navigate("/forgot-password")} className="bg-[#002FA7] hover:bg-[#001d6a] text-white rounded-xl px-8 h-12">
            {t("reset_request_new")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center p-4 sm:p-8" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-between mb-10">
          <Link to="/" className="inline-flex items-center gap-2">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-[#002FA7] to-[#5B21B6] flex items-center justify-center p-1.5">
              <FuturaIcon className="w-full h-full text-white" />
            </div>
            <span className="font-['Outfit'] font-semibold text-xl tracking-tight text-[#111111]">FUTURA</span>
          </Link>
          <LanguageSelector variant="light" />
        </div>

        <div className="animate-fade-in">
          {!done ? (
            <>
              <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-semibold text-[#111111] tracking-tight mb-2" data-testid="reset-title">{t("reset_title")}</h1>
              <p className="text-[#6B7280] mb-6 text-sm">{t("reset_subtitle")}</p>
              {error && <div className="flex items-center gap-2 p-3 mb-4 bg-[#EF4444]/10 text-[#EF4444] rounded-xl" data-testid="reset-error"><AlertCircle className="w-4 h-4 flex-shrink-0" /><p className="text-sm">{error}</p></div>}
              <form onSubmit={handleReset} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-[#111111] font-medium">{t("reset_new_password")}</Label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B7280]" />
                    <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value.replace(/[^A-Za-z0-9]/g, ""))} placeholder={t("reset_new_placeholder")} className="pl-12 h-12 bg-white border-[#E5E5EA] rounded-xl" required minLength={6} pattern="[A-Za-z0-9]{6,}" autoComplete="new-password" data-testid="reset-new-password" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#111111] font-medium">{t("reset_confirm_password")}</Label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B7280]" />
                    <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value.replace(/[^A-Za-z0-9]/g, ""))} placeholder={t("reset_confirm_placeholder")} className="pl-12 h-12 bg-white border-[#E5E5EA] rounded-xl" required minLength={6} pattern="[A-Za-z0-9]{6,}" autoComplete="new-password" data-testid="reset-confirm-password" />
                  </div>
                </div>
                <Button type="submit" disabled={loading} className="w-full h-12 bg-[#002FA7] hover:bg-[#001d6a] text-white rounded-xl" data-testid="reset-submit-btn">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>{t("reset_btn")}<ArrowRight className="ml-2 w-4 h-4" /></>}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-2xl bg-[#10B981]/10 flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-8 h-8 text-[#10B981]" />
              </div>
              <h1 className="font-['Outfit'] text-2xl font-semibold text-[#111111] mb-3" data-testid="reset-done-title">{t("reset_done_title")}</h1>
              <p className="text-[#6B7280] mb-8">{t("reset_done_subtitle")}</p>
              <Button onClick={() => navigate("/login")} className="bg-[#002FA7] hover:bg-[#001d6a] text-white rounded-xl px-8 h-12" data-testid="reset-go-login">
                {t("reset_go_login")}<ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
